import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../../components/feature/SEOHead";

const siteUrl = (import.meta.env.VITE_SITE_URL as string) || "https://example.com";

const schema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      name: "Gaming Brand Network Status Australia",
      url: `${siteUrl}/`,
      inLanguage: "en-AU",
      description:
        "S99AU is currently undergoing scheduled maintenance. We're working hard to improve your experience. Check back soon!",
      publisher: {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        name: "Gaming Brand Network Status Australia",
        url: siteUrl,
      },
    },
    {
      "@type": "WebPage",
      "@id": `${siteUrl}/#webpage`,
      url: `${siteUrl}/`,
      name: "Gaming Brand Network Status Australia",
      isPartOf: { "@id": `${siteUrl}/#website` },
      inLanguage: "en-AU",
      description:
        "S99AU is currently undergoing scheduled maintenance. We're working hard to improve your experience. Check back soon!",
      dateModified: new Date().toISOString().split("T")[0],
    },
    {
      "@type": "ItemList",
      "@id": `${siteUrl}/#brandlist`,
      name: "Gaming Network Brands — Australia",
      description: "Full list of gaming brands in the network.",
      numberOfItems: 7,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "S99AU", url: `${siteUrl}/s99au` },
        { "@type": "ListItem", position: 2, name: "REC99AU", url: `${siteUrl}/rec99au` },
        { "@type": "ListItem", position: 3, name: "FC8AU", url: `${siteUrl}/fc8au` },
        { "@type": "ListItem", position: 4, name: "DD8AU", url: `${siteUrl}/dd8au` },
        { "@type": "ListItem", position: 5, name: "Splinkos", url: `${siteUrl}/splinkos` },
        { "@type": "ListItem", position: 6, name: "Sboomerang", url: `${siteUrl}/sboomerang` },
        { "@type": "ListItem", position: 7, name: "Sbingo9", url: `${siteUrl}/sbingo9` },
      ],
    },
  ],
};

type StatusType = "operational" | "maintenance" | "degraded" | "outage";

interface Brand {
  name: string;
  path: string;
  logo: string;
  accentColor: string;
  glowRgb: string;
  status: StatusType;
  description: string;
}

const STATUS_CONFIG: Record<StatusType, { label: string; color: string; rgb: string; icon: string; bg: string }> = {
  operational: {
    label: "Operational",
    color: "#00E676",
    rgb: "0,230,118",
    icon: "ri-checkbox-circle-fill",
    bg: "rgba(0,230,118,0.1)",
  },
  maintenance: {
    label: "Under Maintenance",
    color: "#FFB300",
    rgb: "255,179,0",
    icon: "ri-tools-fill",
    bg: "rgba(255,179,0,0.1)",
  },
  degraded: {
    label: "Degraded Performance",
    color: "#FF6D00",
    rgb: "255,109,0",
    icon: "ri-error-warning-fill",
    bg: "rgba(255,109,0,0.1)",
  },
  outage: {
    label: "Outage",
    color: "#FF1744",
    rgb: "255,23,68",
    icon: "ri-close-circle-fill",
    bg: "rgba(255,23,68,0.1)",
  },
};

const brands: Brand[] = [
  {
    name: "S99AU",
    path: "/s99au",
    logo: "https://storage.readdy-site.link/project_files/9ee712e0-c50a-4d2b-9956-d4b4346de3f4/7412105e-ed20-425a-a250-0da2f7ef7969_S99_Logo.png?v=e9bb3c1ef0851b808f8058aed7191c08",
    accentColor: "#C9A227",
    glowRgb: "201,162,39",
    status: "operational",
    description: "All systems running smoothly. Platform is fully live and operational.",
  },
  {
    name: "REC99AU",
    path: "/rec99au",
    logo: "https://storage.readdy-site.link/project_files/9ee712e0-c50a-4d2b-9956-d4b4346de3f4/065c2496-05cb-41dd-b2a8-4344a5437f24_rec_logo.png?v=04eb77b63b1a7bd9723821a48b064ad3",
    accentColor: "#5BC8F5",
    glowRgb: "91,200,245",
    status: "operational",
    description: "Fully operational. All services are running at full capacity.",
  },
  {
    name: "FC8AU",
    path: "/fc8au",
    logo: "https://storage.readdy-site.link/project_files/9ee712e0-c50a-4d2b-9956-d4b4346de3f4/b4358a4f-b46f-4c11-af49-3c8e74baee79_FC8_LOGO.png?v=eea0bf80ba223d3fbe4155bc4237aaeb",
    accentColor: "#C8D6E5",
    glowRgb: "200,214,229",
    status: "operational",
    description: "Platform is live and performing at full capacity. No issues detected.",
  },
  {
    name: "DD8AU",
    path: "/dd8au",
    logo: "https://storage.readdy-site.link/project_files/9ee712e0-c50a-4d2b-9956-d4b4346de3f4/eac85ab3-a5b0-4245-a3a6-d3770b3bda50_DD8_Logo.png?v=1528c047acedd0efb9bae55a40478a4a",
    accentColor: "#00FF7F",
    glowRgb: "0,255,127",
    status: "operational",
    description: "All systems online and fully operational. No disruptions reported.",
  },
  {
    name: "Splinkos",
    path: "/splinkos",
    logo: "https://storage.readdy-site.link/project_files/9ee712e0-c50a-4d2b-9956-d4b4346de3f4/7b4d52ff-db18-4dfe-b6b1-ff3b6d5401f0_Splinkos_Logo.png?v=5cb6dc88138261524908c8ba0641eeee",
    accentColor: "#00E5FF",
    glowRgb: "0,229,255",
    status: "operational",
    description: "Live and running normally. All features are available to users.",
  },
  {
    name: "Sboomerang",
    path: "/sboomerang",
    logo: "https://storage.readdy-site.link/project_files/9ee712e0-c50a-4d2b-9956-d4b4346de3f4/d2fada1a-83f7-4962-8c43-3eeb73ac8cd1_Sboomerang_Logo.png?v=3d767f61f910439fbfe8efa8bc26645a",
    accentColor: "#FFD600",
    glowRgb: "255,214,0",
    status: "operational",
    description: "Fully operational. Platform is live with no known issues.",
  },
  {
    name: "Sbingo9",
    path: "/sbingo9",
    logo: "https://storage.readdy-site.link/project_files/9ee712e0-c50a-4d2b-9956-d4b4346de3f4/bbc83503-089b-44e3-adfb-a45e0a31a6e2_SBINGO9LOGO.png?v=6ee39d1b972f96bc1027ca70c7960135",
    accentColor: "#FF1493",
    glowRgb: "255,20,147",
    status: "operational",
    description: "All systems go. Platform is fully live and running at 100%.",
  },
];

function BrandRow({ brand, index }: { brand: Brand; index: number }) {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const cfg = STATUS_CONFIG[brand.status];

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 120 + index * 70);
    return () => clearTimeout(t);
  }, [index]);

  return (
    <div
      className={`rounded-2xl overflow-hidden transition-all duration-500 ease-out cursor-pointer ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
      style={{
        background: "linear-gradient(135deg, #141414 0%, #111111 100%)",
        border: `1px solid ${expanded ? brand.accentColor + "44" : "#ffffff0f"}`,
        transition: "border-color 0.3s ease, opacity 0.5s ease, transform 0.5s ease",
      }}
      onClick={() => setExpanded(!expanded)}
    >
      {/* Main row */}
      <div className="flex items-center gap-4 px-5 py-4">
        {/* Logo */}
        <div
          className="w-28 h-9 flex-shrink-0 flex items-center justify-start"
          style={{ filter: `drop-shadow(0 0 8px rgba(${brand.glowRgb},0.4))` }}
        >
          <img src={brand.logo} alt={brand.name} className="w-full h-full object-contain object-left" />
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Status badge */}
        <div
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap flex-shrink-0"
          style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}33` }}
        >
          {brand.status === "maintenance" ? (
            <i className="ri-tools-fill text-xs" style={{ animation: "spin 5s linear infinite" }}></i>
          ) : brand.status === "operational" ? (
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: cfg.color }}></span>
          ) : (
            <i className={`${cfg.icon} text-xs`}></i>
          )}
          {cfg.label}
        </div>

        {/* Expand chevron */}
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <i
            className="ri-arrow-down-s-line text-gray-600 text-lg transition-transform duration-300"
            style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
          ></i>
        </div>
      </div>

      {/* Expanded detail */}
      <div
        className="overflow-hidden transition-all duration-300"
        style={{ maxHeight: expanded ? "180px" : "0px" }}
      >
        <div className="px-5 pb-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 border-t" style={{ borderColor: "#ffffff08" }}>
          <div className="flex-1 pt-4">
            <p className="text-sm text-gray-400 leading-relaxed">{brand.description}</p>
            <div className="flex items-center gap-2 mt-3">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.color }}></span>
              <span className="text-xs" style={{ color: cfg.color }}>{cfg.label}</span>
            </div>
          </div>
          <Link
            to={brand.path}
            onClick={(e) => e.stopPropagation()}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 hover:opacity-80 mt-3 sm:mt-0"
            style={{ background: `rgba(${brand.glowRgb},0.12)`, color: brand.accentColor, border: `1px solid ${brand.accentColor}33` }}
          >
            View Page
            <i className="ri-external-link-line text-xs"></i>
          </Link>
        </div>
      </div>
    </div>
  );
}

function OverallStatusBanner({ allMaintenance }: { allMaintenance: boolean }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className={`w-full max-w-4xl rounded-2xl px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4 mb-8 transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
      }`}
      style={{
        background: allMaintenance
          ? "linear-gradient(135deg, rgba(255,179,0,0.08) 0%, rgba(255,179,0,0.04) 100%)"
          : "linear-gradient(135deg, rgba(0,230,118,0.08) 0%, rgba(0,230,118,0.04) 100%)",
        border: allMaintenance ? "1px solid rgba(255,179,0,0.2)" : "1px solid rgba(0,230,118,0.2)",
      }}
    >
      <div className="flex items-center gap-3 flex-1">
        <div
          className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0"
          style={{ background: allMaintenance ? "rgba(255,179,0,0.15)" : "rgba(0,230,118,0.15)" }}
        >
          <i
            className={allMaintenance ? "ri-tools-fill text-lg" : "ri-checkbox-circle-fill text-lg"}
            style={{ color: allMaintenance ? "#FFB300" : "#00E676" }}
          ></i>
        </div>
        <div>
          <p className="text-sm font-bold text-white">
            {allMaintenance ? "All Systems Under Maintenance" : "All Systems Operational"}
          </p>
          <p className="text-xs mt-0.5" style={{ color: allMaintenance ? "#FFB300" : "#00E676" }}>
            {allMaintenance
              ? "Scheduled maintenance in progress across the network. We'll be back shortly."
              : "All brands are live and running normally."}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs text-gray-600 flex-shrink-0">
        <i className="ri-refresh-line"></i>
        <span>Auto-refreshes every 60s</span>
      </div>
    </div>
  );
}

export default function Home() {
  const [headerVisible, setHeaderVisible] = useState(false);
  const allMaintenance = brands.every((b) => b.status === "maintenance");
  const operationalCount = brands.filter((b) => b.status === "operational").length;
  const maintenanceCount = brands.filter((b) => b.status === "maintenance").length;

  useEffect(() => {
    const t = setTimeout(() => setHeaderVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <SEOHead
        title="Gaming Brand Network Status Australia"
        description="S99AU is currently undergoing scheduled maintenance. We're working hard to improve your experience. Check back soon!"
        keywords="S99AU, S99, online gaming, maintenance"
        canonicalPath="/"
        schemaJson={schema}
        ogImage="https://storage.readdy-site.link/project_files/9ee712e0-c50a-4d2b-9956-d4b4346de3f4/7412105e-ed20-425a-a250-0da2f7ef7969_S99_Logo.png?v=e9bb3c1ef0851b808f8058aed7191c08"
        ogImageAlt="Gaming Brand Network Status Australia"
        siteName="Gaming Brand Network Status Australia"
      />

      <div className="min-h-screen bg-[#0D0D0D] font-['Inter',sans-serif] relative overflow-hidden">
        {/* Subtle ambient glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-64 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(255,179,0,0.04)" }}></div>

        <main className="relative z-10 flex flex-col items-center px-4 py-14 md:py-20">

          {/* Header */}
          <div
            className={`flex flex-col items-center text-center mb-10 transition-all duration-700 ease-out ${
              headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
            }`}
          >
            <div className="flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 text-xs tracking-widest uppercase font-medium" style={{ background: "rgba(255,179,0,0.08)", color: "#FFB300", border: "1px solid rgba(255,179,0,0.2)" }}>
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#FFB300" }}></span>
              Live Network Status
            </div>

            <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight leading-tight mb-3">
              Brand Status
            </h1>
            <p className="text-gray-500 text-sm md:text-base max-w-lg leading-relaxed">
              Real-time status for all brands across our gaming network. Click any brand to see more details.
            </p>

            {/* Summary counters */}
            <div className="flex items-center gap-6 mt-6">
              <div className="flex items-center gap-2 text-xs">
                <span className="w-2 h-2 rounded-full" style={{ background: "#00E676" }}></span>
                <span className="text-gray-400"><strong className="text-white">{operationalCount}</strong> Operational</span>
              </div>
              <div className="w-px h-4 bg-white/10"></div>
              <div className="flex items-center gap-2 text-xs">
                <span className="w-2 h-2 rounded-full" style={{ background: "#FFB300" }}></span>
                <span className="text-gray-400"><strong className="text-white">{maintenanceCount}</strong> Maintenance</span>
              </div>
              <div className="w-px h-4 bg-white/10"></div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-600">7 Total Brands</span>
              </div>
            </div>
          </div>

          {/* Overall banner */}
          <OverallStatusBanner allMaintenance={allMaintenance} />

          {/* Legend */}
          <div className="w-full max-w-4xl flex items-center justify-between mb-3 px-1">
            <span className="text-xs text-gray-600 uppercase tracking-widest">Brand</span>
            <span className="text-xs text-gray-600 uppercase tracking-widest">Status</span>
          </div>

          {/* Brand rows */}
          <div className="w-full max-w-4xl flex flex-col gap-3">
            {brands.map((brand, i) => (
              <BrandRow key={brand.name} brand={brand} index={i} />
            ))}
          </div>

          {/* Footer */}
          <div
            className={`mt-12 flex flex-col items-center gap-2 transition-all duration-700 delay-700 ${
              headerVisible ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="flex items-center gap-3 text-xs text-gray-700">
              {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                <div key={key} className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: val.color }}></span>
                  <span>{val.label}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-700 mt-1">Status page for our gaming network — Australia</p>
          </div>
        </main>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        `}</style>
      </div>
    </>
  );
}
